import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertCircle, CreditCard } from "lucide-react";
import { PortalHeader, PortalCard, PortalEmpty } from "@/components/portal/PortalHeader";
import { getMyProject, getMyPaymentSummary } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/payments")({
  component: PaymentsPage,
});

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 });

function PaymentsPage() {
  const projectFn = useServerFn(getMyProject);
  const sumFn = useServerFn(getMyPaymentSummary);
  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const sumQ = useQuery({
    queryKey: ["portal", "payments", projectId],
    queryFn: () => sumFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  if (projectQ.isLoading || sumQ.isLoading)
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;
  const { totals, records, provider } = sumQ.data!;

  return (
    <>
      <PortalHeader eyebrow="Payments" title="Balance & history" description="Your proposal total, deposit, payments received, and remaining balance." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
        <Stat label="Project total" value={fmt(totals.total)} />
        <Stat label="Deposit required" value={fmt(totals.deposit)} />
        <Stat label="Payments received" value={fmt(totals.paid)} tone="positive" />
        <Stat label="Remaining balance" value={fmt(totals.balance)} tone="accent" />
      </div>

      {!provider.configured && (
        <div className="rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-4 sm:p-5 mb-5 sm:mb-6 flex items-start gap-3">
          <AlertCircle className="size-5 text-amber-300 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Online payments coming soon</p>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              Payment provider not configured — please pay by check, ACH, or card-on-file via your project manager. All payments are recorded here as soon as they're received.
            </p>
          </div>
        </div>
      )}

      <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Payment history</h2>
      {records.length === 0 ? (
        <PortalEmpty title="No payments recorded yet" description="As payments are received, they'll appear here with a receipt link." />
      ) : (
        <div className="space-y-2">
          {records.map((r) => (
            <PortalCard key={r.id} className="p-3.5 sm:p-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="size-10 shrink-0 rounded-xl bg-white/5 grid place-items-center"><CreditCard className="size-4 text-amber-300" /></div>
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium capitalize truncate">{r.kind.replace("_", " ")}</p>
                  <p className="text-[11px] text-zinc-500 truncate">
                    {new Date(r.recorded_at).toLocaleDateString()} · {r.method}
                    {r.reference && ` · ${r.reference}`}
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-sm font-semibold ${r.kind === "refund" ? "text-red-300" : "text-white"}`}>
                  {r.kind === "refund" ? "−" : ""}{fmt(Number(r.amount))}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{r.status}</p>
              </div>
            </PortalCard>
          ))}
        </div>
      )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "positive" | "accent" }) {
  return (
    <PortalCard className="p-3 sm:p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold leading-tight">{label}</p>
      <p className={`mt-1.5 font-serif text-xl sm:text-2xl break-words ${tone === "positive" ? "text-emerald-300" : tone === "accent" ? "text-amber-300" : "text-white"}`}>{value}</p>
    </PortalCard>
  );
}